import { Component, computed, signal, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { Observable } from "rxjs";
import { finalize, first, map, tap } from "rxjs/operators";

import { LorenBaseFeature } from "../../../shared/base-components/loren-base-feature/loren-base-feature";
import { LorenCompanyUsersService } from "../services/loren-company-users.service";
import { GroupItemModel } from "../../../shared/modules/loren-hierarchy-selector/group.interface";
import { LorenGroupService } from "../../loren-groups/services/loren-group-service/loren-group.service";
import { LorenUserInfo } from "../../../shared/interfaces/loren-user-info.interface";
import { LorenAuthenticationService } from "../../../shared/services/loren-authentication-service/loren-authentication.service";
import { LorenRoleService } from "../../loren-roles/services/loren-role.service";
import { LorenRole } from "../../loren-roles/interfaces/loren-role.interface";

@Component({
  selector: "loren-user-access",
  templateUrl: "./loren-user-access.component.html",
  styleUrls: ["./loren-user-access.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LorenUserAccessComponent extends LorenBaseFeature implements OnInit {
  private lorenAuthenticationService = inject(LorenAuthenticationService);
  private lorenGroupService = inject(LorenGroupService);
  private lorenRoleService = inject(LorenRoleService);
  private companyUsersService = inject(LorenCompanyUsersService);

  selectedUserRoles$: Observable<number[]>;
  roles$ = this.lorenRoleService.allRoles$;
  selectionValue;
  selectedUserGroups = computed<GroupItemModel[]>(
    () =>
      this.lastSelectedUser()
        ?.canAccessGroups.map((x) => this.groupData().find((y) => y.id === x))
        .filter((x) => x) || null
  );
  lastSelectedUser = signal<LorenUserInfo>(null);

  users = signal(null);
  loading = signal<boolean>(false);
  error = signal<boolean>(false);

  private lastSelectedUserRoles: LorenRole[] = [];
  private groupData = signal<GroupItemModel[]>([]);

  ngOnInit() {
    this.loading.set(true);

    this.getUsers();
    this.getGroups();
  }

  handleGroupSelectionChange($event: GroupItemModel[]) {
    const canAccessGroups = $event.map((x) => x.id);

    this.companyUsersService
      .assignGroupsToUser(Number(this.lastSelectedUser().id), canAccessGroups)
      .pipe(first())
      .subscribe({
        next: () =>
          (this.users().find(
            (x) => x.id === this.lastSelectedUser().id
          ).canAccessGroups = canAccessGroups),
      });
  }

  clearUserSelection() {
    this.lastSelectedUser.set(null);
    this.selectionValue = null;
  }

  handleUserSelectionChange(event) {
    const user = event.options[0].value;
    this.lastSelectedUser.set(user);
    this.loadUserRoles(user);
  }

  toggleRoleForUser($event: MatCheckboxChange, role: LorenRole) {
    const roleIds = this.lastSelectedUserRoles.map((x) => x.id);
    let req: Observable<void>;

    if ($event.checked) {
      req = this.lorenRoleService.updateUserRoles$(this.lastSelectedUser().id, [
        ...roleIds,
        role.id,
      ]);
    } else {
      req = this.lorenRoleService.updateUserRoles$(
        this.lastSelectedUser().id,
        roleIds.filter((x) => x !== role.id)
      );
    }

    req
      .pipe(
        first(),
        finalize(() => this.loadUserRoles(this.lastSelectedUser()))
      )
      .subscribe();
  }

  private loadUserRoles(user: LorenUserInfo) {
    this.selectedUserRoles$ = this.lorenRoleService.getUserRoles$(user.id).pipe(
      tap((x) => (this.lastSelectedUserRoles = x)),
      map((x) => x.map((s) => s.id))
    );
  }

  private getUsers() {
    this.companyUsersService.getCompanyUsers().subscribe({
      next: (res) => {
        this.loading.set(false);
        this.error.set(false);
        this.users.set(
          res.items.filter(
            (u) => u.id !== this.lorenAuthenticationService.getUserInfo().id
          )
        );
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      },
    });
  }

  private getGroups() {
    this.lorenGroupService.query().subscribe({
      next: (res) => {
        this.loading.set(false);
        this.error.set(false);
        this.groupData.set(res.items);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      },
    });
  }
}
